/**
 * Product Media Gallery Custom Element
 * Manages Slick slider, zoom functionality, and PhotoSwipe lightbox for product images
 */
if (!customElements.get('product-media-gallery')) {
  class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.lightbox = null;
    this.mainSlider = null;
    this.navSlider = null;
    this.photoSwipeModule = null;
  }

  connectedCallback() {
    // Get data attributes
    this.galleryId = this.dataset.galleryId;
    this.mainSliderId = this.dataset.mainSliderId;
    this.navSliderId = this.dataset.navSliderId;

    // Parse PhotoSwipe config from JSON script tag
    try {
      const configScript = this.querySelector('script[type="application/json"][data-photoswipe-config]');
      if (configScript) {
        const config = JSON.parse(configScript.textContent);
        this.photoswipeLightboxUrl = config.lightboxUrl;
        this.photoswipeUrl = config.photoswipeUrl;
        this.imageData = config.images || [];
      } else {
        this.imageData = [];
      }
    } catch (e) {
      console.error('Failed to parse PhotoSwipe config:', e);
      this.imageData = [];
    }

    // Initialize components
    this.initPhotoSwipe();

    // Wait for jQuery and Slick to be available
    if (typeof jQuery !== 'undefined') {
      this.initSlickSlider();
      this.initZoom();
    } else {
      // Fallback: wait for DOMContentLoaded or load event
      document.addEventListener('DOMContentLoaded', () => {
        this.initSlickSlider();
        this.initZoom();
      });
    }
  }

  disconnectedCallback() {
    // Cleanup PhotoSwipe
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }

    // Cleanup Slick sliders
    if (typeof jQuery !== 'undefined') {
      const $mainSlider = $(`#${this.mainSliderId}`);
      const $navSlider = $(`#${this.navSliderId}`);

      if ($mainSlider.length && $mainSlider.hasClass('slick-initialized')) {
        $mainSlider.slick('unslick');
      }

      if ($navSlider.length && $navSlider.hasClass('slick-initialized')) {
        $navSlider.slick('unslick');
      }

      // Cleanup zoom
      const $zoomableImages = this.querySelectorAll('.enable_zoom');
      $zoomableImages.trigger('zoom.destroy');
    }
  }

  /**
   * Initialize PhotoSwipe lightbox for mobile zoom
   */
  async initPhotoSwipe() {
    if (!this.imageData.length) return;

    try {
      // Use asset URLs from data attributes
      if (!this.photoswipeLightboxUrl || !this.photoswipeUrl) {
        console.warn('PhotoSwipe asset URLs not found in data attributes');
        return;
      }

      // Dynamically import PhotoSwipe modules using Shopify asset URLs
      const PhotoSwipeLightbox = await import(this.photoswipeLightboxUrl);
      const PhotoSwipe = await import(this.photoswipeUrl);

      this.photoSwipeModule = PhotoSwipe.default || PhotoSwipe;

      const options = {
        dataSource: this.imageData,
        pswpModule: () => this.photoSwipeModule,
      };

      this.lightbox = new (PhotoSwipeLightbox.default || PhotoSwipeLightbox)(options);
      this.lightbox.init();

      // Attach click handlers to images
      const imageWrappers = this.querySelectorAll('.c-product-slider__image-wrapper');
      imageWrappers.forEach((wrapper) => {
        wrapper.addEventListener('click', (e) => {
          // Only use lightbox zoom on mobile
          if (window.innerWidth <= 767) {
            const currentIndex = Number(e.currentTarget.dataset.imageIndex);
            this.lightbox.loadAndOpen(currentIndex);
          }
        });
      });
    } catch (error) {
      console.error('Failed to initialize PhotoSwipe:', error);
    }
  }

  /**
   * Initialize Slick slider for main gallery and navigation thumbnails
   */
  initSlickSlider() {
    const $mainSlider = $(`#${this.mainSliderId}`);
    const $navSlider = $(`#${this.navSliderId}`);

    if (!$mainSlider.length || !$navSlider.length) return;

    // Initialize main slider
    $mainSlider.slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      fade: true,
      asNavFor: `#${this.navSliderId}`,
      responsive: [
        {
          breakpoint: 990,
          settings: {
            fade: false,
            dots: true,
            slidesToShow: 2,
            slidesToScroll: 2
          },
        },
        {
          breakpoint: 768,
          settings: {
            fade: false,
            dots: true,
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ]
    });

    // Calculate slides to show for nav slider
    let slidesToShow = 7;
    const navSlides = $navSlider.children().length;
    if (slidesToShow > (navSlides - 1)) {
      slidesToShow = (navSlides - 1);
    }

    // Initialize navigation slider
    $navSlider.slick({
      slidesToShow: slidesToShow,
      slidesToScroll: 1,
      asNavFor: `#${this.mainSliderId}`,
      dots: false,
      arrows: false,
      focusOnSelect: true
    });

    this.mainSlider = $mainSlider;
    this.navSlider = $navSlider;
  }

  /**
   * Initialize zoom functionality for desktop
   */
  initZoom() {
    const $gallery = $(this);
    const $zoomableImages = $gallery.find('.enable_zoom');

    const initZoomPlugin = () => {
      $zoomableImages.zoom({
        magnify: 1.5,
        on: 'click',
        onZoomIn: () => {
          $zoomableImages.css('cursor', 'zoom-out');
        },
        onZoomOut: () => {
          $zoomableImages.css('cursor', 'zoom-in');
        }
      });
    };

    // Only enable zoom on desktop
    if ($(window).width() > 767) {
      initZoomPlugin();

      // Reset zoom on mouse leave
      $gallery.on('mouseleave', () => {
        $zoomableImages.trigger('zoom.destroy');
        initZoomPlugin();
        $zoomableImages.css('cursor', 'zoom-in');
      });
    }

    // Handle window resize
    $(window).on('resize', () => {
      if ($(window).width() <= 767) {
        $zoomableImages.trigger('zoom.destroy');
      } else {
        initZoomPlugin();
      }
    });
  }

  /**
   * Public method to refresh sliders (useful after variant changes)
   */
  refresh() {
    if (this.mainSlider && this.mainSlider.hasClass('slick-initialized')) {
      this.mainSlider.slick('refresh');
    }
    if (this.navSlider && this.navSlider.hasClass('slick-initialized')) {
      this.navSlider.slick('refresh');
    }
  }

  /**
   * Public method to go to specific slide
   */
  goToSlide(index) {
    if (this.mainSlider && this.mainSlider.hasClass('slick-initialized')) {
      this.mainSlider.slick('slickGoTo', index);
    }
  }
  }

  // Register custom element
  customElements.define('product-media-gallery', ProductMediaGallery);
}

